/**
 * SearchResultsController — page /recherche.html (EPIC-099 / 99.80, §15.E).
 *
 * Hydrate la page de résultats de la recherche globale. Lit le terme `q`
 * depuis l'URL (le champ de la topbar soumet vers `/recherche.html?q=…`),
 * appelle `GET /search` et rend les groupes (clients, devis, factures
 * émises, factures reçues, articles) via `<template>` + `cloneTemplate()` +
 * `fillSlots()` — jamais de HTML inline en JS (convention CLAUDE.md).
 *
 * États : chargement (placeholder), résultats groupés, aucun résultat,
 * requête manquante (`?q=` vide). Chaque groupe affiche au plus 5 hits
 * (plafond serveur) + un lien « Voir tous » vers la liste du module filtrée
 * `?q=` quand le total réel dépasse le plafond. Tous les liens pointent vers
 * les pages de détail `.html?id={uuid}` (cf. CLAUDE.md §URLs + 99.60).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

// Lien « Voir tous » par type → liste du module filtrée sur `?q=`.
const LIST_URLS = {
  client: "/clients.html",
  quote: "/devis.html",
  invoice: "/factures.html",
  purchase_invoice: "/factures-recues.html",
  article: "/catalogue.html",
};

export default class SearchResultsController extends BaseController {
  static targets = ["heading", "summary", "results", "loading", "empty", "missing"];

  connect() {
    const params = new URLSearchParams(window.location.search);
    const q = (params.get("q") ?? "").trim();

    if (q === "") {
      this._showMissing();
      return;
    }

    if (this.hasHeadingTarget) {
      this.headingTarget.textContent = `Résultats pour « ${q} »`;
    }
    this._fetch(q);
  }

  async _fetch(q) {
    this._showState("loading");
    try {
      const result = await api.search(q);
      this._render(result);
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "La recherche a échoué. Réessayez dans un instant.";
      this.showError(msg);
      this._renderError(q);
    }
  }

  _render(result) {
    const groups = Array.isArray(result?.groups) ? result.groups : [];
    const totalCount = Number(result?.totalCount) || 0;
    const query = result?.query ?? "";

    if (totalCount === 0 || groups.length === 0) {
      this._showEmpty(query);
      return;
    }

    this._clearResults();
    const labels = [];
    for (const group of groups) {
      labels.push(group.label);
      this.resultsTarget.appendChild(this._buildGroup(group));
    }

    if (this.hasSummaryTarget) {
      const noun = totalCount > 1 ? "résultats trouvés" : "résultat trouvé";
      this.summaryTarget.textContent = `${totalCount} ${noun} dans ${labels.join(", ")}.`;
    }
    this._showState("results");
  }

  _buildGroup(group) {
    const fragment = this.cloneTemplate("tpl-search-group");
    const total = Number(group.total) || 0;
    const items = Array.isArray(group.items) ? group.items : [];
    const noun = total > 1 ? "résultats" : "résultat";

    this.fillSlots(fragment, { groupTitle: `${group.label} — ${total} ${noun}` });

    const list = fragment.querySelector('[data-slot-element="list"]');
    if (list) {
      for (const item of items) {
        list.appendChild(this._buildItem(item));
      }
    }

    const more = fragment.querySelector('[data-slot-element="more"]');
    if (more) {
      const listUrl = LIST_URLS[group.type];
      if (listUrl && total > items.length) {
        more.setAttribute("href", `${listUrl}?q=${encodeURIComponent(this._query())}`);
        more.textContent = `Voir tous les résultats ${group.label} →`;
        more.hidden = false;
      } else {
        more.hidden = true;
      }
    }

    return fragment;
  }

  _buildItem(item) {
    const fragment = this.cloneTemplate("tpl-search-item");
    this.fillSlots(fragment, {
      itemTitle: item.label ?? "—",
      itemMeta: this._buildMeta(item),
    });
    const link = fragment.querySelector('[data-slot-element="link"]');
    if (link && item.url) link.setAttribute("href", item.url);
    return fragment;
  }

  _buildMeta(item) {
    const parts = [];
    if (item.sublabel) parts.push(item.sublabel);
    if (item.amount !== null && item.amount !== undefined) {
      parts.push(this._formatAmount(item.amount));
    }
    if (item.date) parts.push(this.formatShortDate(item.date));
    return parts.join(" · ");
  }

  _formatAmount(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "";
    return `${num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }

  _renderError(query) {
    if (this.hasSummaryTarget) this.summaryTarget.textContent = "Erreur de chargement.";
    this._showEmpty(query);
  }

  _showEmpty(query) {
    if (this.hasHeadingTarget && query) {
      this.headingTarget.textContent = `Résultats pour « ${query} »`;
    }
    this._showState("empty");
  }

  _showMissing() {
    this._showState("missing");
  }

  /** Affiche l'un des 4 états exclusifs, masque les autres. */
  _showState(active) {
    const states = {
      loading: this.hasLoadingTarget ? this.loadingTarget : null,
      results: this.hasResultsTarget ? this.resultsTarget : null,
      empty: this.hasEmptyTarget ? this.emptyTarget : null,
      missing: this.hasMissingTarget ? this.missingTarget : null,
    };
    for (const [key, el] of Object.entries(states)) {
      if (el) el.hidden = key !== active;
    }
  }

  _clearResults() {
    if (!this.hasResultsTarget) return;
    while (this.resultsTarget.firstChild) {
      this.resultsTarget.removeChild(this.resultsTarget.firstChild);
    }
  }

  _query() {
    return (new URLSearchParams(window.location.search).get("q") ?? "").trim();
  }
}
